import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'icon';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

const BASE =
  'inline-flex items-center justify-center rounded-btn font-semibold transition-all duration-normal ease-smooth ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-accent-primary text-white hover:bg-accent-info',
  secondary:
    'bg-surface-hover/60 text-text-primary border border-accent-primary/30 hover:border-accent-primary/50 hover:bg-surface-muted/60 backdrop-blur-sm',
  ghost:
    'bg-transparent text-text-secondary hover:text-accent-primary hover:bg-surface-hover/50',
  danger:
    'bg-danger text-white hover:bg-danger/90',
  icon:
    'bg-surface-hover/60 text-text-secondary border border-border hover:text-accent-primary hover:border-accent-primary/30 hover:bg-surface-muted/60 aspect-square',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs gap-1.5',
  md: 'px-field-x py-field-y text-sm gap-2',
  lg: 'px-6 py-3 text-base gap-2.5',
};

// Icon button paddings dobrane tak, żeby height icon variant matchował
// height zwykłego Button o tym samym size. Button md: text-sm line-height
// 20px + py-field-y 10px×2 = 40px. Icon md: w-4 (16px) + p-3 12px×2 = 40px.
const ICON_SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'p-2',   // 32px × 32px (match Button sm)
  md: 'p-3',   // 40px × 40px (match Button md — zrównane z non-icon md)
  lg: 'p-3.5', // 44px × 44px (match Button lg)
};

const ICON_EL_SIZE: Record<ButtonSize, string> = {
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

export interface ButtonVariantsOpts {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
}

/**
 * Zwraca sam className dla buttona - przydatne gdy stylujesz inny element
 * jak button (np. <Link className={buttonVariants({ variant: 'primary' })}>).
 */
export function buttonVariants({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
}: ButtonVariantsOpts = {}): string {
  const sizeClass = variant === 'icon' ? ICON_SIZE_CLASSES[size] : SIZE_CLASSES[size];
  return [BASE, VARIANT_CLASSES[variant], sizeClass, fullWidth ? 'w-full' : '']
    .filter(Boolean)
    .join(' ');
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const iconSize = ICON_EL_SIZE[size];
  const isIconOnly = variant === 'icon';

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={[buttonVariants({ variant, size, fullWidth }), className].filter(Boolean).join(' ')}
      {...rest}
    >
      {loading ? (
        <Loader2 className={`${iconSize} shrink-0 animate-spin`} />
      ) : leftIcon ? (
        <span
          className={`inline-flex shrink-0 items-center justify-center ${iconSize} [&>svg]:w-full [&>svg]:h-full`}
          aria-hidden="true"
        >
          {leftIcon}
        </span>
      ) : null}
      {!isIconOnly && children}
      {!loading && rightIcon ? (
        <span
          className={`inline-flex shrink-0 items-center justify-center ${iconSize} [&>svg]:w-full [&>svg]:h-full`}
          aria-hidden="true"
        >
          {rightIcon}
        </span>
      ) : null}
    </button>
  );
});
